/**
 * Weekly meal plan ingredient aggregation → shopping list items.
 *
 * PURE core (no DB): receives resolved plan entries (recipe +
 * ingredients) and returns aggregated lines. See PRODUCT_PLAN.md
 * ("Ingredient aggregation").
 */

import { normalizeIngredientName, normalizeUnit, toDisplayQuantity, unitFamilyKey } from "./units";

export interface PlannedIngredient {
  /** Freeform ingredient name ("Tomato sauce"). */
  name: string;
  /** Quantity for the recipe BASE servings; null = "to taste". */
  quantity: number | null;
  unit: string | null;
  /** Aisle/category name assigned to the ingredient (denormalized). */
  category?: string | null;
  /** Base servings of the recipe it belongs to. */
  recipeServings: number;
  /** Planned servings in the plan entry (null = recipe default). */
  plannedServings?: number | null;
}

export interface PantryStock {
  name: string;
  quantity: number | null;
  unit: string | null;
}

export interface AggregatedItem {
  /** Display name (first occurrence, with its original capitalization). */
  name: string;
  /** Quantity converted to best display unit; null = no quantity. */
  quantity: number | null;
  unit: string | null;
  category: string | null;
  /** Stable key name+family to preserve checked state on regeneration. */
  key: string;
}

interface Bucket {
  displayName: string;
  family: ReturnType<typeof normalizeUnit>;
  total: number;
  hasQuantity: boolean;
  category: string | null;
  key: string;
}

/** Scaling factor for a plan entry. */
export function servingsFactor(recipeServings: number, plannedServings?: number | null): number {
  if (!plannedServings || recipeServings <= 0) {
    return 1;
  }
  return plannedServings / recipeServings;
}

/** Stable aggregation key: normalized name + unit family. */
export function aggregationKey(name: string, unit: string | null | undefined): string {
  const normalized = normalizeUnit(unit);
  return `${normalizeIngredientName(name)}|${unitFamilyKey(normalized)}`;
}

/**
 * Aggregates planned ingredients:
 *  1. scales by servings (plannedServings / recipeServings),
 *  2. converts to the base unit of its family and sums by (name, family),
 *  3. subtracts pantry stock (if provided) clamped to 0 — fully covered
 *     items disappear,
 *  4. presents each line in its best unit (g→kg, ml→l).
 */
export function aggregateIngredients(
  ingredients: PlannedIngredient[],
  pantry: PantryStock[] = [],
): AggregatedItem[] {
  const buckets = new Map<string, Bucket>();

  for (const ing of ingredients) {
    const trimmedName = ing.name.trim();
    if (!trimmedName) {
      continue;
    }
    const normalized = normalizeUnit(ing.unit);
    const key = `${normalizeIngredientName(trimmedName)}|${unitFamilyKey(normalized)}`;

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        displayName: trimmedName,
        family: normalized,
        total: 0,
        hasQuantity: false,
        category: ing.category?.trim() || null,
        key,
      };
      buckets.set(key, bucket);
    }
    if (!bucket.category && ing.category?.trim()) {
      bucket.category = ing.category.trim();
    }

    if (ing.quantity !== null && ing.quantity !== undefined) {
      const factor = servingsFactor(ing.recipeServings, ing.plannedServings);
      bucket.total += ing.quantity * factor * normalized.factor;
      bucket.hasQuantity = true;
    }
  }

  // Pantry: subtract stock by the same key (name + family).
  for (const stock of pantry) {
    const normalized = normalizeUnit(stock.unit);
    const key = `${normalizeIngredientName(stock.name)}|${unitFamilyKey(normalized)}`;
    const bucket = buckets.get(key);
    if (!bucket) {
      continue;
    }
    if (stock.quantity === null || stock.quantity === undefined) {
      // Stock without quantity = "I have it" → fully covers the ingredient.
      buckets.delete(key);
      continue;
    }
    if (bucket.hasQuantity) {
      bucket.total -= stock.quantity * normalized.factor;
      if (bucket.total <= 0) {
        buckets.delete(key);
      }
    }
  }

  const items: AggregatedItem[] = [];
  for (const bucket of buckets.values()) {
    if (!bucket.hasQuantity) {
      items.push({
        name: bucket.displayName,
        quantity: null,
        unit: null,
        category: bucket.category,
        key: bucket.key,
      });
      continue;
    }
    const display = toDisplayQuantity(bucket.total, bucket.family.family, bucket.family.raw);
    items.push({
      name: bucket.displayName,
      quantity: display.quantity,
      unit: display.unit,
      category: bucket.category,
      key: bucket.key,
    });
  }

  // Stable order: category (alphabetical, no category last) + name.
  items.sort((a, b) => {
    const catA = a.category ?? "￿";
    const catB = b.category ?? "￿";
    if (catA !== catB) {
      return catA.localeCompare(catB);
    }
    return a.name.localeCompare(b.name);
  });

  return items;
}
